import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { useUser } from '#/contexts/UserEnvironmentContext';
import { useI18n } from '#/i18n/hooks/useI18n';
import { useAuthTmpStorageContext } from '#/modules/auth/context/AuthTmpStorageContext';
import type { CmsApiSubscribe } from '#/modules/cms/api/types/CmsApiSubscribe';
import { RichError } from '#/utils/RichError';
import { postJson } from '#/utils/http/postJson';
import { trackError } from '#/utils/trackError';
import { isUrlWithProtocol } from '#/utils/url/isUrlWithProtocol';
import { showModal } from '#/zustand-stores/modalStore';

type Props = {
  blockId: string;
  index: number;
  redirectUrl?: string;
};

export function useCmsSubscribe({ blockId, index, redirectUrl }: Props) {
  const { i18n } = useI18n();
  const navigate = useNavigate();
  const { user } = useUser();
  const [isSubscribed, setIsSubscribed] = useState(false);
  const { tmpStorage, clearTmpStorage } = useAuthTmpStorageContext();

  const subscribe = useCallback(() => {
    const showEmailSignupModal = (cmsSubscribe: CmsApiSubscribe) => {
      showModal('EMAIL_SIGNUP', {
        title: i18n('Create an account on Idealist in order to subscribe'),
        tmpStorage: { type: 'cmsSubscribe', cmsSubscribe },
        onLogin: () => {
          const postBaseUrl = '/data/website/button-link-campaign-signup/';
          postJson(postBaseUrl + blockId, {
            index,
          });

          if (typeof redirectUrl === 'string') {
            if (isUrlWithProtocol(redirectUrl)) {
              // Replace current page so that, when using the browser's back button
              // the app has the proper state, with user logged in
              window.location.replace(window.location.href);
              window.location.href = redirectUrl;
              return;
            }

            navigate(
              redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`,
            );
          } else {
            setIsSubscribed(true);
          }
        },
      });
    };

    const loggedInVerifiedWithInfo = Boolean(
      user &&
      user.email !== null &&
      user.emailVerified &&
      user.firstName &&
      user.lastName,
    );

    if (loggedInVerifiedWithInfo) {
      const postBaseUrl = '/data/website/button-link-campaign-signup/';
      postJson(postBaseUrl + blockId, {
        index,
      })
        .then(() => {
          if (tmpStorage?.type === 'cmsSubscribe' && user?.emailVerified) {
            const { cmsSubscribe } = tmpStorage;
            clearTmpStorage();

            if (typeof cmsSubscribe.redirectUrl !== 'string') {
              setIsSubscribed(true);
              return;
            }

            if (isUrlWithProtocol(cmsSubscribe.redirectUrl)) {
              window.location.href = cmsSubscribe.redirectUrl;
              return;
            }

            navigate(
              cmsSubscribe.redirectUrl.startsWith('/')
                ? cmsSubscribe.redirectUrl
                : `/${cmsSubscribe.redirectUrl}`,
            );
            return;
          }

          if (typeof redirectUrl === 'string') {
            if (isUrlWithProtocol(redirectUrl)) {
              window.location.href = redirectUrl;
              return;
            }

            navigate(
              redirectUrl.startsWith('/') ? redirectUrl : `/${redirectUrl}`,
            );
          } else {
            setIsSubscribed(true);
          }
        })
        .catch((e) => {
          trackError(
            // @ts-expect-error TS(2345): Argument of type 'string' is not assignable to par... Remove this comment to see the full error message
            new RichError('Error subscribing in CMS block', JSON.stringify(e)),
          );
        });
    } else {
      showEmailSignupModal({ blockId, index, redirectUrl });
    }
  }, [
    i18n,
    user,
    blockId,
    index,
    tmpStorage,
    redirectUrl,
    navigate,
    clearTmpStorage,
  ]);

  useEffect(() => {
    if (
      tmpStorage?.type === 'cmsSubscribe' &&
      tmpStorage.cmsSubscribe.blockId === blockId &&
      tmpStorage.cmsSubscribe.index === index &&
      user?.emailVerified
    ) {
      subscribe();
    }
  }, [user?.emailVerified, blockId, index, subscribe, tmpStorage]);

  return { subscribe, isSubscribed };
}
